import { FC, useRef } from "react";
import { getWheelSegments } from "../model/wheelSegments";
import type { WheelColor } from "../model/wheelSegments";
import { useWheelRotation } from "./useWheelRotation";

import styles from "./RaffleWheel.module.css";

export type WheelState = "idle" | "spinning" | "stopped";

interface Props {
  state: WheelState;
}

// Сколько колесо тормозит после розыгрыша до полной остановки.
export const WHEEL_SPIN_MS = 20000;

const SIZE = 400;
const CENTER = SIZE / 2;
const SEGMENTS = getWheelSegments(20, CENTER, 172);

// От тёмного у ступицы к светлому на ободе.
const GRADIENTS: Record<WheelColor, [string, string]> = {
  red: ["#7a1616", "#ff4b4b"],
  green: ["#06573a", "#12c47c"],
  white: ["#8c8c8c", "#f5f5f5"],
  black: ["#0a0a0a", "#2b2b2b"],
};

// Колесо декоративное: победителей выбирает бэк, секторы с людьми не связаны.
export const RaffleWheel: FC<Props> = ({ state }) => {
  const rotorRef = useRef<SVGGElement>(null);
  useWheelRotation(rotorRef, state, WHEEL_SPIN_MS);

  return (
    <svg
      className={styles.wheel}
      viewBox={`0 0 ${SIZE} ${SIZE}`}
      aria-hidden="true"
    >
      <defs>
        {Object.entries(GRADIENTS).map(([color, [inner, outer]]) => (
          <radialGradient
            key={color}
            id={`wheel-${color}`}
            gradientUnits="userSpaceOnUse"
            cx={CENTER}
            cy={CENTER}
            r={172}
          >
            <stop offset="0.25" stopColor={inner} />
            <stop offset="1" stopColor={outer} />
          </radialGradient>
        ))}
      </defs>
      <circle className={styles.ring} cx={CENTER} cy={CENTER} r={190} />
      <g ref={rotorRef} className={styles.rotor}>
        <circle className={styles.rim} cx={CENTER} cy={CENTER} r={178} />
        {SEGMENTS.map(({ color, path }) => (
          <path
            key={path}
            className={styles.segment}
            d={path}
            fill={`url(#wheel-${color})`}
          />
        ))}
        {SEGMENTS.map(({ peg }) => (
          <circle
            key={`${peg.x}-${peg.y}`}
            className={styles.peg}
            cx={peg.x}
            cy={peg.y}
            r={4}
          />
        ))}
      </g>
      <circle className={styles.hub} cx={CENTER} cy={CENTER} r={44} />
      <image
        href="/assets/svg/logo.svg"
        x={CENTER - 22}
        y={CENTER - 22}
        width={44}
        height={44}
      />
      <path
        className={styles.pointer}
        d={`M${CENTER - 18} 2H${CENTER + 18}L${CENTER} 40Z`}
      />
    </svg>
  );
};
